import { useDirectorsByCorporation } from '@commenda/api';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Icons,
  LoadingIndicator,
} from '@commenda/kakashi';
import { ServiceRequestDetails } from '@commenda/types/src/service-request/types';
import { Country, File, LegacyDocumentType } from '@prisma/client';
import { Country as CountryLib } from 'country-state-city';
import { DataPoint } from '../../../../Corporation/common/DataPoint';
import { viewUploadedFile } from '../../../utils';

export type DirectorDetails = {
  directorFirstName: string;
  directorMiddleName?: string;
  directorLastName: string;
  number: string;
  email: string;
  personalDetails?: {
    nationality?: string;
    panCardNumber?: string;
    aadharCardNumber?: string;
    passportNumber?: string;
    passportCountryOfIssue?: Country;
  };
};

type DirectorDetailsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  calendarLink: string;
  serviceRequest?: ServiceRequestDetails;
  directorId?: number;
  isViewOnly?: boolean;
};

export const DirectorDetailsModal = ({
  isOpen,
  onClose,
  calendarLink,
  serviceRequest,
  directorId,
  isViewOnly = false,
}: DirectorDetailsModalProps) => {
  const { data: directorsData, error: directorsError } =
    useDirectorsByCorporation(serviceRequest?.corporation?.id, undefined);

  const directorDetails = directorsData?.find((dir) => dir.id === directorId);
  const corpHasDirector = directorId
    ? serviceRequest?.corporation?.corporationHasDirector?.find(
        (chd) => chd.director.id === directorId
      )
    : undefined;

  const getLatestFile = (documentType: LegacyDocumentType): File | null => {
    if (documentType === LegacyDocumentType.PanCard && directorDetails?.panCard)
      return directorDetails.panCard;

    if (
      documentType === LegacyDocumentType.AadharCard &&
      directorDetails?.aadharCard
    )
      return directorDetails.aadharCard;

    const getLatestFromDocuments = (
      documents: { documentType: LegacyDocumentType; files?: File[] }[]
    ): File | null => {
      const matchingDoc = documents.find(
        (doc) => doc.documentType === documentType
      );
      if (matchingDoc?.files?.length) {
        return matchingDoc.files.sort((a, b) => b.id - a.id)[0];
      }
      return null;
    };

    const filesFromDirectorFiles = directorDetails?.directorFiles?.filter(
      (file): file is File => file.documentType === documentType
    );
    if (filesFromDirectorFiles?.length) {
      return filesFromDirectorFiles.sort((a, b) => b.id - a.id)[0];
    }

    if (directorDetails?.directorCorporationDocuments?.length) {
      const file = getLatestFromDocuments(
        directorDetails.directorCorporationDocuments
      );
      if (file) return file;
    }

    if (directorDetails?.directorDocuments?.length) {
      const file = getLatestFromDocuments(directorDetails.directorDocuments);
      if (file) return file;
    }

    if (corpHasDirector?.documents?.length) {
      const file = getLatestFromDocuments(corpHasDirector.documents);
      if (file) return file;
    }

    return null;
  };

  const passportFile = getLatestFile(LegacyDocumentType.Passport);
  const panCardFile = getLatestFile(LegacyDocumentType.PanCard);
  const aadharCardFile = getLatestFile(LegacyDocumentType.AadharCard);

  const isIndianNational =
    directorDetails?.personalDetails?.nationality === Country.IN;
  const isForeignNational =
    directorDetails?.personalDetails?.nationality &&
    directorDetails?.personalDetails?.nationality !== Country.IN;

  const countryName = directorDetails?.personalDetails?.nationality
    ? CountryLib.getCountryByCode(directorDetails.personalDetails.nationality)
        ?.name
    : 'Unknown';

  const fullName = [
    directorDetails?.directorFirstName,
    directorDetails?.directorMiddleName,
    directorDetails?.directorLastName,
  ]
    .filter(Boolean)
    .join(' ');

  const handleProceedToBooking = () => {
    window.open(calendarLink, '_blank');
    onClose();
  };

  if (directorsError || !directorDetails) {
    if (directorsError) console.error(directorsError);
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <LoadingIndicator />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-md bg-bg-brand-section flex justify-center items-center">
              <Icons.User01 className="w-6 h-6 text-text-brand" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg-semibold text-text-secondary">
                Director Details for DSC Procurement
              </h3>
              <p className="text-sm-regular text-text-tertiary mt-1">
                {isViewOnly
                  ? "View the director's information and documents for DSC procurement"
                  : "Please review the director's information before scheduling the DSC procurement meeting"}
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4 px-6 overflow-y-auto max-h-[calc(95vh-200px)]">
          <div>
            <h4 className="text-sm-semibold text-text-secondary mb-3">
              Basic Information
            </h4>
            <div className="flex flex-col border border-border-primary rounded-md overflow-clip divide-y divide-border-primary">
              <DataPoint title="Name" value={fullName} hideAddAction />
              <DataPoint
                title="Phone Number"
                value={directorDetails.number}
                hideAddAction
              />
              <DataPoint
                title="Email ID"
                value={directorDetails.email}
                hideAddAction
              />
              <DataPoint
                title="Nationality"
                value={countryName}
                hideAddAction
              />
            </div>
          </div>

          {isIndianNational && (
            <div>
              <h4 className="text-sm-semibold text-text-secondary mb-3">
                Indian National - Required Documents
              </h4>
              <div className="flex flex-col border border-border-primary rounded-md overflow-clip divide-y divide-border-primary">
                <DataPoint
                  title="PAN Card"
                  value={panCardFile?.id ? '' : 'N/A'}
                  hideAddAction
                  reactNode={
                    panCardFile?.id ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => viewUploadedFile(panCardFile.id)}
                        className="h-8"
                      >
                        <Icons.File06 className="size-4" />
                        View document
                      </Button>
                    ) : undefined
                  }
                />
                <DataPoint
                  title="Aadhaar Card"
                  value={aadharCardFile?.id ? '' : 'N/A'}
                  hideAddAction
                  reactNode={
                    aadharCardFile?.id ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => viewUploadedFile(aadharCardFile.id)}
                        className="h-8"
                      >
                        <Icons.File06 className="size-4" />
                        View document
                      </Button>
                    ) : undefined
                  }
                />
              </div>
            </div>
          )}

          {isForeignNational && (
            <div>
              <h4 className="text-sm-semibold text-text-secondary mb-3">
                Foreign National - Required Documents
              </h4>
              <div className="flex flex-col border border-border-primary rounded-md overflow-clip divide-y divide-border-primary">
                <DataPoint
                  title="Passport"
                  value={passportFile?.id ? '' : 'N/A'}
                  hideAddAction
                  reactNode={
                    passportFile?.id ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => viewUploadedFile(passportFile.id)}
                        className="h-8"
                      >
                        <Icons.File06 className="size-4" />
                        View document
                      </Button>
                    ) : undefined
                  }
                />
              </div>
            </div>
          )}

          <Alert
            variant="warning"
            title="Important Information"
            description="Please have the director's documents ready during the DSC procurement meeting. The vendor will require access to the documents mentioned above to complete the process."
          />
        </div>

        <DialogFooter>
          {isViewOnly ? (
            <Button variant="secondary" size="md" onClick={onClose}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="secondary" size="md" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleProceedToBooking}
              >
                <Icons.Calendar className="w-4 h-4" />
                Proceed to Schedule Meeting
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
